/**
 * @fileoverview Componente responsável por gerenciar filtros, abas e listagens de tickets.
 */

import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import GroupIcon from "@material-ui/icons/Group";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import TicketsToolbarActions from "../TicketsToolbarActions";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { Can } from "../Can";
import { QueuesFilter } from "../QueuesFilter";
import { useConversationQueues } from "../../hooks/useTicketVisibility";
import usePermission from "../../hooks/usePermission";
import { measureDialogSide } from "../../utils/dialogSidePlacement";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem, Checkbox, ListItemText } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import FilterListIcon from "@material-ui/icons/FilterList";
import { Bell, Eye, Plus, Search, SlidersHorizontal } from "lucide-react";

const COMPACT_TOOLBAR_WIDTH = 450;
const useStyles = makeStyles(theme => ({
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		width: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		borderRadius:0,
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(22, 22, 24, 0.95) 0%, rgba(26, 26, 28, 0.95) 100%)'
			: '#FFFFFF',
	},

	tabsHeader: {
		flex: "none",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.tabHeaderBackground,
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.15)'
			: '1px solid rgba(0, 0, 0, 0.12)',
		padding: '12px 16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		minHeight: '48px',
		flexWrap: 'nowrap',
		gap: '6px',
		[theme.breakpoints.down('md')]: {
			padding: '12px 12px',
		},
		[theme.breakpoints.down('sm')]: {
			padding: '10px 10px',
			gap: '4px',
		},
	},

	tabsInternal: {
		flex: "none",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.tabHeaderBackground,
	},

	settingsIcon: {
		alignSelf: "center",
		marginLeft: "auto",
		padding: 8,
	},

	tabsContainer: {
		display: 'inline-flex',
		alignItems: 'stretch',
		backgroundColor: theme.mode === 'dark' ? 'rgba(42, 42, 42, 0.9)' : '#ffffff',
		borderRadius: '999px',
		padding: '3px',
		width: 'auto',
		minWidth: 0,
		height: '32px',
		boxSizing: 'border-box',
		border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid #f0f0f0',
		flexShrink: 1,
		overflow: 'hidden',
		[theme.breakpoints.down('sm')]: {
			height: '28px',
			padding: '2px',
		},
		'& .MuiTabs-root': {
			minHeight: 0,
			height: '100%',
			width: '100%',
		},
		'& .MuiTabs-scroller': {
			height: '100%',
		},
		'& .MuiTabs-flexContainer': {
			height: '100%',
			alignItems: 'stretch',
			gap: '2px',
		},
	},

	tab: {
		flex: '0 1 auto',
		minWidth: 0,
		minHeight: 0,
		width: 'auto',
		height: '100%',
		padding: '0 12px',
		margin: '0',
		boxSizing: 'border-box',
		borderRadius: '999px',
		backgroundColor: 'transparent',
		color: theme.mode === 'dark' ? '#b0b0b0' : '#666',
		fontWeight: 500,
		fontSize: '0.85rem',
		lineHeight: 1,
		textTransform: 'none',
		whiteSpace: 'nowrap',
		transition: 'background-color 150ms ease, color 150ms ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		[theme.breakpoints.down('sm')]: {
			padding: '0 9px',
			fontSize: '0.78rem',
		},
		'&:hover': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
			color: theme.mode === 'dark' ? '#e0e0e0' : '#333',
		},
		'&.Mui-selected': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(58, 58, 58, 0.95)' : '#f0f0f0',
			color: theme.mode === 'dark' ? '#e0e0e0' : '#333333',
			fontWeight: 600,
		},
		'& .MuiTab-wrapper': {
			fontSize: 'inherit',
			fontWeight: 'inherit',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: '100%',
		},
	},

	internalTab: {
		minWidth: 120,
		width: 120,
		padding: 5
	},

	searchButton: {
		marginLeft: '12px',
		padding: '8px',
		color: theme.palette.mode === 'light' ? '#666' : '#ccc',
		'&:hover': {
			backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#404040',
			borderRadius: '50%',
		}
	},

	ticketOptionsBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.optionsBackground,
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.1)'
			: '1px solid rgba(0, 0, 0, 0.12)',
		padding: theme.spacing(1),
		fontSize: '0.75rem',
		'& .MuiButton-root': {
			fontSize: '0.7rem',
			padding: '4px 4px',
			minHeight: '28px'
		},
		'& .MuiFormControlLabel-root': {
			'& .MuiFormControlLabel-label': {
				fontSize: '0.7rem',
			}
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem'
		},
		'& .MuiFormControl-root': {
			'& .MuiSelect-root': {
				fontSize: '0.8rem',
				minHeight: '28px',
				padding: '4px 4px',
				textAlign: 'center',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'start'
			},
			'& .MuiOutlinedInput-input': {
				padding: '4px 4px',
				paddingLeft: '8px',
				textAlign: 'center'
			}
		}
	},

	ticketSearchLine: {
		padding: theme.spacing(1),
	},

	serachInputWrapper: {
		flex: 1,
		background: theme.palette.total,
		display: "flex",
		borderRadius: 40,
		padding: 4,
		marginRight: theme.spacing(1),
	},

	searchIcon: {
		color: "grey",
		marginLeft: 6,
		marginRight: 6,
		alignSelf: "center",
		width: 16,
		height: 16,
		strokeWidth: 1.75,
		flexShrink: 0,
	},

	searchInput: {
		flex: 1,
		border: "none",
		borderRadius: 30,
		fontSize: '0.85rem',
		'& input': {
			fontSize: '0.85rem',
			padding: '6px 0',
			'&::placeholder': {
				fontSize: '0.85rem',
				opacity: 0.7,
			},
		},
	},

	insiderTabPanel: {
		height: '100%',
		marginTop: "-72px",
		paddingTop: "72px",
		overflow: "hidden",
	},

	insiderDoubleTabPanel: {
		display:"flex",
		flexDirection: "column",
		marginTop: "-72px",
		paddingTop: "72px",
		height: "100%",
		overflow: "hidden",
	},

	ticketsListContainer: {
		width: "100%",
		height: "100%",
		overflow: "auto",
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},

	ticketsListInner: {
		display: "block",
		width: "100%",
		height: "100%",
		overflow: "auto",
	},

	labelContainer: {
		width: "auto",
		padding: 0
	},
	iconLabelWrapper: {
		flexDirection: "row",
		'& > *:first-child': {
			marginBottom: '3px !important',
			marginRight: 16
		}
	},
	insiderTabLabel: {
		[theme.breakpoints.down(1600)]: {
			display:'none'
		}
	},
	smallFormControl: {
		'& .MuiOutlinedInput-input': {
			padding: "12px 10px",
		},
		'& .MuiInputLabel-outlined': {
			marginTop: "-6px"
		}
	},

	subTab: {
		minHeight: '48px',
		padding: '12px 24px',
		fontSize: '0.7rem',
		fontWeight: 500,
		marginRight: '24px',
		// O indicador acompanha a aba por CSS, sem depender da medição em pixels do MUI.
		'&.Mui-selected::after': {
			content: '""',
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: 0,
			height: 2,
			backgroundColor: theme.palette.primary.main,
			pointerEvents: 'none',
		},
		'& .MuiTab-wrapper': {
			fontSize: '0.7rem',
			fontWeight: 500,
		},
		'& .MuiBadge-root': {
			fontSize: '0.7rem',
		},
		'& .MuiBadge-badge': {
			fontSize: '0.65rem',
			height: '18px',
			minWidth: '18px',
		}
	},

	badge: {
		'& .MuiBadge-badge': {
			color: '#fff',
			backgroundColor: '#6EC263',
			minWidth: '20px',
			height: '20px',
			borderRadius: '10px',
			padding: '0 6px',
			fontSize: '0.75rem'
		}
	},
	
	newTicketButton: {
		width: 40,
		height: 40,
		minWidth: 40,
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
		}
	},

	floatingPlusButton: {
		position: 'absolute',
		bottom: '24px',
		left: '24px',
		width: 56,
		height: 56,
		minWidth: 56,
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
		zIndex: 1000,
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
			boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
			transform: 'scale(1.05)',
		},
		'& .MuiButton-label': {
			padding: 0,
			fontSize: '1.5rem',
			fontWeight: 300,
		},
		transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},

	iconsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		marginLeft: 'auto',
		flexShrink: 0,
		[theme.breakpoints.down('sm')]: {
			gap: '2px',
		},
	},

	clickableIcon: {
		padding: '4px',
		width: '28px',
		height: '28px',
		minWidth: '28px',
		borderRadius: '4px',
		border: 'none',
		color: '#666666',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
		[theme.breakpoints.down('sm')]: {
			padding: '3px',
			width: '24px',
			height: '24px',
			minWidth: '24px',
			'& svg': {
				width: '14px',
				height: '14px',
			},
		},
		'&:hover': {
			backgroundColor: '#f8f8f8',
			color: '#333333',
			borderColor: '#e0e0e0',
		},
		'&.active': {
			backgroundColor: 'transparent',
			color: theme.palette.primary.main,
			borderColor: theme.palette.primary.main,
		},
		'&.active:hover': {
			backgroundColor: '#f8f8f8',
			color: theme.palette.primary.dark,
			borderColor: theme.palette.primary.dark,
		},
		'& svg': {
			width: '16px',
			height: '16px',
			strokeWidth: 1.75,
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem',
		},
	},

	searchIconButton: {
		padding: '4px',
		width: '28px',
		height: '28px',
		minWidth: '28px',
		borderRadius: '4px',
		border: 'none',
		color: '#444444',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: '#f8f8f8',
			color: '#222222',
			borderColor: '#e0e0e0',
		},
		'& svg': {
			width: '16px',
			height: '16px',
			strokeWidth: 1.75,
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem',
		},
	},

	unifiedFiltersContainer: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		'& > *': {
			width: '100%',
		},
	},
	filterDropdownFormControl: {
		width: '100%',
	},
	filterDropdownSelect: {
		backgroundColor: theme.mode === 'light' ? '#f8fafc' : 'rgba(39, 39, 42, 0.95)',
		borderRadius: '14px',
		transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
		fontWeight: 500,
		cursor: 'pointer',
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.mode === 'light' ? '#e2e8f0' : 'rgba(91, 124, 255, 0.3)',
			borderWidth: '1.5px',
			transition: 'border-color 0.2s ease',
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.mode === 'light' ? theme.palette.primary.main : 'rgba(91, 124, 255, 0.7)',
			boxShadow: 'none',
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.palette.primary.main,
			borderWidth: '2px',
			boxShadow: theme.mode === 'dark'
				? '0 0 0 1px rgba(91, 124, 255, 0.15)'
				: '0 0 0 1px rgba(61, 92, 253, 0.1)',
		},
		'& .MuiSelect-select': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			padding: '14px 16px',
			cursor: 'pointer',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-start',
			textAlign: 'left',
			fontWeight: 500,
			'&:focus': {
				backgroundColor: 'transparent',
				borderRadius: '14px',
			},
		},
		'& .MuiSvgIcon-root': {
			color: theme.mode === 'light' ? '#64748b' : '#5B7CFF',
		},
	},
	filterDropdownTextField: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'light' ? '#f8fafc' : 'rgba(39, 39, 42, 0.95)',
			borderRadius: '14px',
			transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.mode === 'light' ? '#e2e8f0' : 'rgba(91, 124, 255, 0.3)',
				borderWidth: '1.5px',
				transition: 'border-color 0.2s ease',
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.mode === 'light' ? theme.palette.primary.main : 'rgba(91, 124, 255, 0.7)',
				boxShadow: 'none',
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.palette.primary.main,
				borderWidth: '2px',
				boxShadow: theme.mode === 'dark'
					? '0 0 0 1px rgba(91, 124, 255, 0.15)'
					: '0 0 0 1px rgba(61, 92, 253, 0.1)',
			},
		},
		'& .MuiAutocomplete-inputRoot': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			padding: '4px 12px',
			minHeight: '50px',
			alignItems: 'center',
			justifyContent: 'flex-start',
			textAlign: 'left',
		},
		'& .MuiInputBase-input': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			fontWeight: 500,
			textAlign: 'left',
			'&::placeholder': {
				color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
				opacity: 1,
			},
		},
		'& .MuiSvgIcon-root': {
			color: theme.mode === 'light' ? '#64748b' : '#5B7CFF',
		},
	},
	filterDropdownAutocompleteRoot: {
		width: '100%',
	},
	filterDropdownAutocompletePopper: {
		zIndex: 10000,
	},
	// O paper do Autocomplete reaproveita o visual do menu do Select
	// (filterDropdownMenuPaper), mas lá a rolagem é do próprio paper. Aqui quem
	// rola é o listbox interno do MUI — se o paper também limitar a altura,
	// aparecem DUAS barras de rolagem. Então: paper sem limite/rolagem, e o
	// limite de 250px (mesmo do Select) vai para o listbox.
	// `&.MuiPaper-root` dobra a especificidade: filterDropdownMenuPaper vem
	// depois na folha e também usa !important, então só a ordem não bastaria.
	filterDropdownAutocompletePaper: {
		'&.MuiPaper-root': {
			maxHeight: 'none !important',
			overflowY: 'visible',
		},
	},
	filterDropdownAutocompleteListbox: {
		maxHeight: '250px',
		overflowY: 'auto',
		padding: '8px',
	},
	filterDropdownAutocompleteOption: {
		color: theme.mode === 'dark' ? '#F5F5F5' : '#1e293b',
		fontSize: '0.875rem',
		padding: '10px 14px',
		borderRadius: '8px',
		margin: '2px 4px',
		minHeight: 'auto',
		transition: 'background-color 0.2s ease, color 0.2s ease',
		'&[data-focus="true"]': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.15)' : 'rgba(61, 92, 253, 0.08)',
		},
		'&[aria-selected="true"]': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.25)' : 'rgba(61, 92, 253, 0.12)',
		},
	},
	filterDropdownMenuPaper: {
		backgroundColor: theme.mode === 'dark' ? 'rgba(24, 24, 27, 1) !important' : '#ffffff',
		borderRadius: '12px',
		border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid #e2e8f0',
		marginTop: '4px',
		maxHeight: '250px !important',
		overflowY: 'auto',
		zIndex: 9999,
		boxShadow: theme.mode === 'dark'
			? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(91, 124, 255, 0.2)'
			: '0 4px 12px rgba(0, 0, 0, 0.1)',
		'& .MuiList-root': {
			padding: '8px !important',
		},
		'& .MuiMenuItem-root': {
			color: theme.mode === 'dark' ? '#F5F5F5' : '#1e293b',
			fontSize: '0.875rem',
			padding: '10px 14px !important',
			borderRadius: '8px',
			margin: '2px 4px !important',
			minHeight: 'auto !important',
			transition: 'background-color 0.2s ease, color 0.2s ease',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.15)' : 'rgba(61, 92, 253, 0.08)',
			},
			'&.Mui-selected': {
				backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.25)' : 'rgba(61, 92, 253, 0.12)',
				'&:hover': {
					backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.35)' : 'rgba(61, 92, 253, 0.16)',
				},
			},
		},
	},
	connectionOptionName: {
		fontWeight: 500,
	},
	connectionStatusRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		fontSize: '0.75rem',
		color: theme.mode === 'dark' ? '#a1a1aa' : '#64748b',
	},
	connectionStatusDot: {
		width: '8px',
		height: '8px',
		borderRadius: '50%',
		flexShrink: 0,
	},
	filterDialog: {
		'& .MuiDialog-paper': {
			background: theme.palette.type === 'dark'
				? 'linear-gradient(135deg, rgba(18, 18, 19, 1) 0%, rgba(22, 22, 24, 1) 100%)'
				: '#ffffff',
			border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
			borderRadius: '24px',
			boxShadow: theme.palette.type === 'dark'
				? '0 8px 32px rgba(0, 0, 0, 0.4)'
				: '0 8px 32px rgba(0, 0, 0, 0.1)',
			width: '420px',
			maxWidth: '90vw',
		},
	},
	filterDialogTitle: {
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(91, 124, 255, 0.1) 0%, rgba(157, 124, 255, 0.1) 100%)'
			: 'linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(138, 35, 135, 0.05) 100%)',
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.2)'
			: '1px solid rgba(0, 0, 0, 0.08)',
		color: theme.palette.type === 'dark' ? '#ffffff' : '#1a1a1a',
		fontWeight: 600,
		padding: '16px 20px 12px 20px',
		fontSize: '1.125rem',
	},
	filterDialogContent: {
		padding: '16px 20px',
	},
	filterDialogActions: {
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(15, 15, 17, 0.5) 0%, rgba(20, 20, 22, 0.5) 100%)'
			: '#fafafa',
		borderTop: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.2)'
			: '1px solid rgba(0, 0, 0, 0.08)',
		padding: '12px 20px 16px 20px',
		gap: '8px',
	},
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  // "Mostrar todos" começa ligado para todos os perfis: o atendente também vê as
  // conversas dos colegas (o servidor só lista as das filas dele) e pode
  // desligar no olho do topo.
  const [showAllTickets, setShowAllTickets] = useState(true);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const searchInputRef = useRef();
  const searchTimeoutRef = useRef(null);
  const tabsHeaderRef = useRef(null);
  const compactToolbarRef = useRef(false);
  const [compactToolbar, setCompactToolbar] = useState(false);
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { currentTicket } = useContext(TicketsContext);
  const { can } = usePermission();
  const canCreateTicket = can("tickets.create", true);
  // Com a regra nova, as conversas de grupos e as sem fila têm permissão própria
  // (sem ela, o servidor não as mostra): a aba Grupos e o "Sem fila" do filtro somem.
  const canSeeGroups = can("tickets.view_groups", true);
  const canSeeNoQueue = can("tickets.view_no_queue", true);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingHasMore, setPendingHasMore] = useState(false);

  // Quem vê quais conversas (nível da empresa): as filas que o filtro oferece e
  // a seleção padrão (hooks/useTicketVisibility); o servidor confere o mesmo.
  const { visibility, filterQueues, defaultQueues } = useConversationQueues();
  // Tanto admin quanto user enxergam tickets "Sem fila" por padrão (sentinela
  // 0 do TicketsQueueSelect). A opção sempre apareceu no dropdown pra todos,
  // só vinha desmarcada pra user comum — agora vem marcada também, fica
  // explícito e o atendente vê tickets sem fila atribuída sem precisar
  // marcar manualmente a opção.
  // Sem tickets.view_no_queue (regra nova), o "Sem fila" continua na seleção
  // (sem a opção no filtro): traz as sem fila que estão com a pessoa, e o
  // servidor não manda as outras.
  const initialQueueIds = [...defaultQueues.map((q) => q.id), 0];
  const [selectedQueueIds, setSelectedQueueIds] = useState(initialQueueIds);
  const [selectedTags, setSelectedTags] = useState([]);
  const [withoutTags, setWithoutTags] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);

  // Estados temporários para filtros (antes de aplicar)
  const [tempSelectedQueueIds, setTempSelectedQueueIds] = useState(initialQueueIds);
  // A seleção padrão acompanha o nível e as filas da empresa (chegam depois do
  // primeiro desenho) até o atendente aplicar um filtro. A do filtro aberto não
  // muda (ela é copiada da aplicada ao abrir).
  const defaultQueueIdsKey = JSON.stringify(initialQueueIds);
  const queueFilterAppliedRef = useRef(false);
  useEffect(() => {
    if (queueFilterAppliedRef.current) return;
    setSelectedQueueIds(JSON.parse(defaultQueueIdsKey));
  }, [defaultQueueIdsKey]);
  const [tempSelectedTags, setTempSelectedTags] = useState([]);
  const [tempWithoutTags, setTempWithoutTags] = useState(false);
  const [tempSelectedUsers, setTempSelectedUsers] = useState([]);
  const [tempSelectedWhatsappIds, setTempSelectedWhatsappIds] = useState([]);

  const [unreadOnly, setUnreadOnly] = useState(false);

  useLayoutEffect(() => {
    const header = tabsHeaderRef.current;
    if (!header) return undefined;
    const measure = () => {
      const width = header.clientWidth;
      if (!width) return;
      // A decisão acompanha o divisor, inclusive em monitores grandes.
      const compact = width < COMPACT_TOOLBAR_WIDTH;
      if (compact === compactToolbarRef.current) return;
      compactToolbarRef.current = compact;
      setCompactToolbar(compact);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(header);
    else window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // A aba Grupos sumiu (o acesso foi relido): volta para Atendendo.
  useEffect(() => {
    if (tab === "groups" && !canSeeGroups) setTab("open");
  }, [tab, canSeeGroups]);

  // Sincronizar aba com o status do ticket atual
  // Quando um ticket é reaberto (code: "#open"), mudar para aba "open"
  useEffect(() => {
    if (currentTicket?.code === "#open") {
      setTab("open");
      setTabOpen("open");
    }
  }, [currentTicket]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
  }, [tab]);

  const handleSearch = useCallback((e) => {
    const searchedTerm = e.target.value.toLowerCase();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleChangeTab = useCallback((e, newValue) => {
    setTab(newValue);
  }, []);

  const handleChangeTabOpen = useCallback((e, newValue) => {
    setTabOpen(newValue);
  }, []);

  const handleViewPending = useCallback(() => {
    setTabOpen("pending");
  }, []);

  const handlePendingCount = useCallback((count, { hasMore } = {}) => {
    setPendingCount(count);
    setPendingHasMore(Boolean(hasMore));
  }, []);

  const handleCloseOrOpenTicket = useCallback((ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  }, [history]);

  // Handlers temporários (não aplicam o filtro imediatamente)
  // Recebe { tagIds, withoutTags } do TagsFilter (popover) e atualiza ambos temps.
  const handleTempSelectedTags = useCallback(({ tagIds, withoutTags: wt }) => {
    setTempSelectedTags(tagIds);
    setTempWithoutTags(wt);
  }, []);

  const handleTempSelectedUsers = useCallback((selecteds) => {
    const users = selecteds.map((t) => t.id);
    setTempSelectedUsers(users);
  }, []);

  const normalizeMultiSelectValues = (values) => {
    const rawValues = Array.isArray(values) ? values : String(values).split(",");
    return rawValues
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  };

  const getConnectionStatusInfo = (status) => {
    const normalized = String(status || "").toUpperCase();
    const connectedStates = new Set(["CONNECTED", "WORKING", "PAIRING"]);
    const isConnected = connectedStates.has(normalized);

    return {
      label: isConnected ? "Conectada" : "Desconectada",
      color: isConnected ? "#22c55e" : "#ef4444",
    };
  };

  // Desktop (>= md): os dropdowns do modal de Filtros abrem ao lado direito do
  // modal em vez de por cima dele. No mobile não há espaço lateral: ficam abaixo.
  const muiTheme = useTheme();
  const filterPlacement = useMediaQuery(muiTheme.breakpoints.up("md")) ? "right" : "bottom";
  // Select de conexão: offset para o menu abrir ao lado do modal, medido ao
  // abrir (menu tem a largura do Select); null = abaixo, como os outros filtros.
  const [connMenuSide, setConnMenuSide] = useState(null);
  const handleConnMenuOpen = (event) => {
    const el = event?.currentTarget;
    setConnMenuSide(filterPlacement === "right" && el ? measureDialogSide(el, el.offsetWidth) : null);
  };

  const filterAutocompleteClasses = useMemo(
    () => ({
      root: classes.filterDropdownAutocompleteRoot,
      popper: classes.filterDropdownAutocompletePopper,
      paper: `${classes.filterDropdownMenuPaper} ${classes.filterDropdownAutocompletePaper}`,
      listbox: classes.filterDropdownAutocompleteListbox,
      option: classes.filterDropdownAutocompleteOption,
    }),
    [classes]
  );

  // Aplicar filtros (quando clicar no botão Aplicar)
  const handleApplyFilters = useCallback(() => {
    // Sem nenhuma fila marcada (nem "Sem fila") a query cai num `queueId IN ()`
    // e o backend acaba devolvendo tudo — bloqueamos no cliente com aviso.
    if (!tempSelectedQueueIds || tempSelectedQueueIds.length === 0) {
      toast.warn(i18n.t("tickets.filters.emptyQueueWarning"));
      return;
    }
    queueFilterAppliedRef.current = true;
    setSelectedQueueIds(tempSelectedQueueIds);
    setSelectedTags(tempSelectedTags);
    setWithoutTags(tempWithoutTags);
    setSelectedUsers(tempSelectedUsers);
    setSelectedWhatsappIds(tempSelectedWhatsappIds);
    setShowFiltersModal(false);
  }, [tempSelectedQueueIds, tempSelectedTags, tempWithoutTags, tempSelectedUsers, tempSelectedWhatsappIds]);

  // Cancelar filtros (quando clicar no botão Cancelar)
  const handleCancelFilters = useCallback(() => {
    // Reseta os temporários para os valores aplicados
    setTempSelectedQueueIds(selectedQueueIds);
    setTempSelectedTags(selectedTags);
    setTempWithoutTags(withoutTags);
    setTempSelectedUsers(selectedUsers);
    setTempSelectedWhatsappIds(selectedWhatsappIds);
    setShowFiltersModal(false);
  }, [selectedQueueIds, selectedTags, withoutTags, selectedUsers, selectedWhatsappIds]);

  // Quando abrir o modal, sincroniza temp com valores aplicados
  const handleOpenFiltersModal = useCallback(() => {
    setTempSelectedQueueIds(selectedQueueIds);
    setTempSelectedTags(selectedTags);
    setTempWithoutTags(withoutTags);
    setTempSelectedUsers(selectedUsers);
    setTempSelectedWhatsappIds(selectedWhatsappIds);
    setShowFiltersModal(true);
  }, [selectedQueueIds, selectedTags, withoutTags, selectedUsers, selectedWhatsappIds]);

  const toolbarActions = [
    {
      id: "unread", label: i18n.t("tickets.toolbar.unread"), icon: <NotificationsNoneIcon />,
      menuIcon: <Bell />,
      checked: unreadOnly, onClick: () => setUnreadOnly((previous) => !previous),
    },
    {
      id: "all", label: i18n.t("tickets.buttons.showAll"), icon: <Eye />,
      checked: showAllTickets, onClick: () => setShowAllTickets((previous) => !previous),
    },
    {
      id: "filters", label: i18n.t("tickets.toolbar.filters"), icon: <FilterListIcon />,
      menuIcon: <SlidersHorizontal />,
      onClick: handleOpenFiltersModal,
    },
    {
      id: "search", label: i18n.t("tickets.toolbar.search"), icon: <Search />,
      className: classes.searchIconButton, onClick: () => setTab("search"),
    },
  ];
  const renderToolbar = (actions) => (
    <TicketsToolbarActions
      actions={actions}
      compact={compactToolbar}
      containerClassName={classes.iconsContainer}
      buttonClassName={classes.clickableIcon}
    />
  );

  return (
    <div className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <div ref={tabsHeaderRef} className={classes.tabsHeader}>
        <div className={classes.tabsContainer}>
          <Tabs
            value={tab === "search" ? false : tab}
            onChange={handleChangeTab}
            variant="standard"
            TabIndicatorProps={{ style: { display: "none" } }}
            textColor="inherit"
            aria-label="icon label tabs example"
            style={{ minHeight: 'auto' }}
          >
            <Tab
              value={"open"}
              label={i18n.t("tickets.tabs.open.title")}
              classes={{ root: classes.tab }}
            />
            {canSeeGroups && (
              <Tab
                value={"groups"}
                label={i18n.t("tickets.tabs.groups.title")}
                classes={{ root: classes.tab }}
              />
            )}
            <Tab
              value={"closed"}
              label={i18n.t("tickets.tabs.closed.title")}
              classes={{ root: classes.tab }}
            />
          </Tabs>
        </div>
        {/* O olho "Mostrar todos" some no nível "Só as próprias" (não há
            conversa de colega para mostrar). */}
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => renderToolbar(visibility === "own" ? toolbarActions.filter((action) => action.id !== "all") : toolbarActions)}
          no={() => renderToolbar(toolbarActions.filter((action) => action.id !== "all"))}
        />
      </div>
      {tab === "search" && (
        <div className={classes.ticketOptionsBox}>
          <div className={classes.serachInputWrapper}>
            <Search className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        </div>
      )}

      {/* Nova conversa: hoje, aberta a todos; com a regra nova, tickets.create. */}
      {canCreateTicket && (
        <Button
          className={classes.floatingPlusButton}
          onClick={() => setNewTicketModalOpen(true)}
        >
          <Plus />
        </Button>
      )}
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          TabIndicatorProps={{ style: { display: "none" } }}
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={openCount}
                color="primary"
              >
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value={"open"}
            classes={{ root: classes.subTab }}
          />
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={pendingCount}
                color="primary"
              >
                {i18n.t("ticketsList.pendingHeaderTab")}
              </Badge>
            }
            value={"pending"}
            classes={{ root: classes.subTab }}
          />
        </Tabs>
        <div className={classes.ticketsListContainer}>
          <div className={classes.ticketsListInner} style={{ display: tabOpen === "open" ? "block" : "none" }}>
            <TicketsList
              status="open"
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              selectedWhatsappIds={selectedWhatsappIds}
              updateCount={(val) => setOpenCount(val)}
              pendingCount={pendingCount}
              pendingHasMore={pendingHasMore}
              onViewPending={handleViewPending}
              onAdjustFilters={handleOpenFiltersModal}
              withUnreadMessages={tabOpen === "open" && unreadOnly ? "true" : undefined}
              disableItemRendering={tabOpen !== "open"}
              tags={selectedTags}
              withoutTags={withoutTags}
              users={selectedUsers}
            />
          </div>
          <div className={classes.ticketsListInner} style={{ display: tabOpen === "pending" ? "block" : "none" }}>
            <TicketsList
              status="pending"
              selectedQueueIds={selectedQueueIds}
              selectedWhatsappIds={selectedWhatsappIds}
              updateCount={handlePendingCount}
              onAdjustFilters={handleOpenFiltersModal}
              withUnreadMessages={tabOpen === "pending" && unreadOnly ? "true" : undefined}
              disableItemRendering={tabOpen !== "pending"}
              tags={selectedTags}
              withoutTags={withoutTags}
              users={selectedUsers}
            />
          </div>
        </div>
      </TabPanel>
      {canSeeGroups && (
        <TabPanel value={tab} name="groups" className={classes.ticketsWrapper}>
          <TicketsList
            status="groups"
            onAdjustFilters={handleOpenFiltersModal}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            withUnreadMessages={tab === "groups" && unreadOnly ? "true" : undefined}
            tags={selectedTags}
            withoutTags={withoutTags}
            users={selectedUsers}
          />
        </TabPanel>
      )}
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          onAdjustFilters={handleOpenFiltersModal}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedWhatsappIds={selectedWhatsappIds}
          tags={selectedTags}
          withoutTags={withoutTags}
          users={selectedUsers}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          onAdjustFilters={handleOpenFiltersModal}
          showAll={true}
          tags={selectedTags}
          withoutTags={withoutTags}
          users={selectedUsers}
          selectedWhatsappIds={selectedWhatsappIds}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>

      <Dialog
        open={showFiltersModal}
        onClose={handleCancelFilters}
        maxWidth={false}
        className={classes.filterDialog}
      >
        <DialogTitle className={classes.filterDialogTitle}>
          Filtros
        </DialogTitle>

        <DialogContent className={classes.filterDialogContent}>
          <div className={classes.unifiedFiltersContainer}>
            <FormControl
              fullWidth
              margin="dense"
              variant="outlined"
              className={classes.filterDropdownFormControl}
            >
              <Select
                multiple
                displayEmpty
                className={classes.filterDropdownSelect}
                value={tempSelectedWhatsappIds}
                onChange={(e) => setTempSelectedWhatsappIds(normalizeMultiSelectValues(e.target.value))}
                onOpen={handleConnMenuOpen}
                MenuProps={{
                  classes: { paper: classes.filterDropdownMenuPaper },
                  PaperProps: connMenuSide != null ? { style: { marginTop: 0 } } : undefined,
                  anchorOrigin: connMenuSide != null
                    ? { vertical: "top", horizontal: connMenuSide }
                    : { vertical: "bottom", horizontal: "left" },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => i18n.t("tickets.filters.connection")}
              >
                {(whatsApps || []).map((whatsapp) => {
                  const statusInfo = getConnectionStatusInfo(whatsapp.status);

                  return (
                  <MenuItem dense key={whatsapp.id} value={whatsapp.id}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={tempSelectedWhatsappIds.indexOf(whatsapp.id) > -1}
                    />
                    <ListItemText
                      primary={<span className={classes.connectionOptionName}>{whatsapp.name}</span>}
                      secondary={
                        <span className={classes.connectionStatusRow}>
                          <span
                            className={classes.connectionStatusDot}
                            style={{ backgroundColor: statusInfo.color }}
                          />
                          {statusInfo.label}
                        </span>
                      }
                    />
                  </MenuItem>
                )})}
              </Select>
            </FormControl>

            {/* Mesmo layout do filtro por Etiquetas (FilterPopover compartilhado). */}
            <div style={{ marginTop: 8, marginBottom: 4 }}>
              <QueuesFilter
                selectedQueueIds={tempSelectedQueueIds}
                userQueues={filterQueues}
                onChange={(values) => setTempSelectedQueueIds(values)}
                placeholder={i18n.t("tickets.filters.queues")}
                fullWidth
                placement={filterPlacement}
                showNoQueue={canSeeNoQueue}
              />
            </div>

            <div style={{ marginTop: 8, marginBottom: 4 }}>
              <TagsFilter
                selectedTagIds={tempSelectedTags}
                withoutTags={tempWithoutTags}
                onChange={handleTempSelectedTags}
                placeholder={i18n.t("tickets.filters.tagsPlaceholder")}
                title={i18n.t("tickets.filters.tagsPopoverTitle")}
                searchPlaceholder={i18n.t("tickets.filters.tagsSearchPlaceholder")}
                noTagsLabel={i18n.t("tickets.filters.noTagsOption")}
                noTagsFoundLabel={i18n.t("tickets.filters.noTagsFound")}
                singleSelectedLabel={i18n.t("tickets.filters.singleSelected")}
                multipleSelectedLabel={i18n.t("tickets.filters.multipleSelected")}
                showingAllLabel={i18n.t("tickets.filters.showingAll")}
                selectAllLabel={i18n.t("tickets.filters.selectAll")}
                clearLabel={i18n.t("tickets.filters.clear")}
                loadingLabel={i18n.t("tickets.filters.loadingTags")}
                fullWidth
                placement={filterPlacement}
              />
            </div>

            <UsersFilter
              onFiltered={handleTempSelectedUsers}
              initialUsers={tempSelectedUsers}
              autocompleteClasses={filterAutocompleteClasses}
              textFieldClassName={classes.filterDropdownTextField}
              placeholderText="Filtrar por Usuários"
              containerStyle={{ padding: 0, marginTop: 8, marginBottom: 4 }}
              placement={filterPlacement}
            />
          </div>
        </DialogContent>

        <DialogActions className={classes.filterDialogActions}>
          <Button
            onClick={handleCancelFilters}
            style={{
              color: '#6b7280',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '6px 16px',
              borderRadius: '6px'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            color="primary"
            style={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              borderRadius: '6px',
              padding: '6px 16px',
              boxShadow: 'none'
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketsManagerTabs;
